import { UPPER_ALPHABET } from 'constants/alphabets';
import { NULL_REPORT_ID } from 'constants/report';

import * as React from 'react';
import { useState, useEffect } from 'react';

import { gql, useQuery } from '@apollo/client';
import MultiProjectReportsQuery from '@graphql/queries/MultiProjectReportsQuery';
import ProjectByIdQuery from '@graphql/queries/ProjectByIdQuery';
import QaPreviewReportQuery, {
  QaPreviewReportResponse,
} from '@graphql/queries/QaPreviewReportQuery';
import { KeyboardBackspace as KeyboardBackspaceIcon } from '@mui/icons-material';
import { IconButton, Divider } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import MaterialButton from 'components/MaterialButton';
import ReportMarketMap from 'components/Report/Maps/ReportMarketMap';
import ReportTextureMap from 'components/Report/Maps/ReportMarketMap/ReportTextureMap';
import ProductMarketPreference from 'components/Report/ProductMarketPreference';
import {
  ProductVersion,
  ProductVersionSet,
} from 'components/Report/ProductVersion';
import ReportSummaryComparison from 'components/Report/ReportSummary/ReportSummaryComparison';
import { getProductName } from 'components/Report/utils';
import { QaPreviewReportProvider } from 'hooks/QaPreviewReportContext';
import { multiProjectCustomNames } from 'hooks/useCustomProductNames';
import useQaPreviewSession from 'hooks/useQaPreviewSession';
import useWindowSize from 'hooks/useWindowSize';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RouterProps } from 'react-router';
import selectViewerUserId from 'selectors/viewerUserId';
import selectWorkspaceProducerId from 'selectors/workspaceProducerId';
import { useCustomerPreferences } from 'services/customerPreferences';
import { nullCheck, checkIsWrongWorkspace } from 'utils/afsUtils';

import ComparisonPicker, { ComparisonPickerOption } from './ComparisonPicker';
import styles from './ProjectComparison.module.css';
import {
  buildComparisonReports,
  ProjectComparisonReportInfo,
  withPreviewProject,
} from './utils';
import LoadingScreen from '../../components/LoadingScreen';
import FlavorDecompositionComparison from '../../components/Report/FlavorDecomposition/FlavorDecompositionComparison/FlavorDecompositionComparison';
import {
  MapOptions,
  getDefaultMapOptions,
} from '../../components/Report/Maps/MapOptions';
import GastrographRadar from '../../components/Report/ReportGastrographRadar/ReportGastrographRadar';
import i18n from '../../i18n';
import { PageHeader } from '../../styles/themeComponents';

export const COL_WIDTH = 480;

export enum ComparisonTabs {
  FLAVOR_PROFILE,
  FLAVOR_DECOMPOSITION,
  PQ_DISTRIBUTION,
  DIVIDER,
  STACK_RANK,
  MARKET_MAP,
  TEXTURE,
}

const WORKSPACE_PROJECTS_QUERY = gql`
  query ProjectsByWorkspace($workspaceId: Int) {
    allProjects(condition: { workspaceId: $workspaceId }) {
      nodes {
        id
        name
      }
    }
  }
`;

const isProductCompareTab = (tabIndex: number) => {
  switch (tabIndex) {
    case ComparisonTabs.STACK_RANK:
    case ComparisonTabs.MARKET_MAP:
    case ComparisonTabs.TEXTURE:
      return false;
    default:
      return true;
  }
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props;

  return <div>{value === index && children}</div>;
}

const useComparisonQaPreview = (reportId: string, projectId: string) => {
  const { t } = useTranslation();
  const qaPreview = useQaPreviewSession(reportId, projectId);
  const { data, loading } = useQuery<QaPreviewReportResponse>(
    QaPreviewReportQuery,
    { variables: { reportId: qaPreview?.reportId }, skip: !qaPreview },
  );
  const previewReportJob = data?.reports.nodes[0];
  const suffix = ` — ${t('reportsQa.preview.reportSuffix')}`;
  return {
    qaPreview,
    previewLoading: loading,
    previewReportJob,
    previewProjectId: qaPreview ? parseInt(projectId) : undefined,
    previewLabel: {
      suffix,
      project: (previewReportJob?.projectName ?? '') + suffix,
    },
  };
};

interface Props extends RouterProps {
  match: Record<string, any>;
}

const ProjectComparisonContent: React.FC<Props> = (props) => {
  const { match, history: browserHistory } = props;
  const { projectId }: { projectId: string } = match.params;

  /* Optionally passed in values from Report Summary screen */
  const {
    firstReportId,
    firstProductId,
    firstProductVersion,
    secondReportId,
    secondProductId,
    secondProductVersion,
  } = match.params;

  const { t } = useTranslation();

  const { width } = useWindowSize();

  const viewerUserId = useSelector((state) => selectViewerUserId(state));
  const producerId = useSelector((state) => selectWorkspaceProducerId(state));

  // QA reviewers comparing from a report preview bypass the project/workspace check
  const {
    qaPreview,
    previewLoading,
    previewReportJob,
    previewProjectId,
    previewLabel,
  } = useComparisonQaPreview(firstReportId, projectId);

  const { data: projectCheck, loading: projectCheckLoading } = useQuery<{
    project: { id: number; workspaceId: number } | null;
  }>(ProjectByIdQuery, {
    variables: { projectId: parseInt(projectId) },
    skip: !!qaPreview,
  });

  const isWrongWorkspace =
    !qaPreview &&
    checkIsWrongWorkspace(parseInt(projectId), projectCheck, producerId);

  const customerPreferences = useCustomerPreferences(
    isWrongWorkspace || projectCheckLoading,
  );

  interface ProductToCompareMap extends reports.ProductToCompare {
    projectId: number;
    mapOptions: MapOptions;
  }

  const [selectedProducts, setSelectedProducts] = useState<
    ProductToCompareMap[]
  >([
    {
      projectId: parseInt(projectId),
      reportId: firstReportId ?? '',
      productId: parseInt(firstProductId) ?? null,
      version: nullCheck(firstProductVersion),
      reportProducts: [],
      mapOptions: getDefaultMapOptions(customerPreferences),
    },
    {
      projectId: parseInt(projectId),
      reportId: secondReportId ?? '',
      productId: parseInt(secondProductId) ?? null,
      version: nullCheck(secondProductVersion),
      reportProducts: [],
      mapOptions: getDefaultMapOptions(customerPreferences),
    },
  ]);

  const customProductNames = multiProjectCustomNames(
    selectedProducts.map((p) => p.projectId),
    isWrongWorkspace || projectCheckLoading,
    qaPreview,
  );

  const { loading, error, data } = useQuery<reports.MultiProjectReport>(
    MultiProjectReportsQuery,
    {
      variables: { projectIDs: selectedProducts.map((p) => p.projectId) },
      fetchPolicy: 'no-cache',
      skip: isWrongWorkspace || projectCheckLoading,
    },
  );

  const [projectName, setProjectName] = useState<string>('');
  const [projectReports, setProjectReports] = useState<
    ProjectComparisonReportInfo[]
  >([]);
  const [currentTabIndex, setCurrentTabIndex] = useState(
    ComparisonTabs.FLAVOR_PROFILE,
  );
  const [workspaceProjects, setWorkspaceProjects] = useState<
    Array<{ id: number; name: string }>
  >([]);

  const { loading: projectsLoading, error: projectsError } = useQuery(
    WORKSPACE_PROJECTS_QUERY,
    {
      variables: {
        workspaceId: parseInt(producerId),
      },
      fetchPolicy: 'no-cache',
      skip: isWrongWorkspace || projectCheckLoading,
      onCompleted: (data) => setWorkspaceProjects(data.allProjects.nodes ?? []),
    },
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTabIndex(newValue);
  };

  useEffect(() => {
    if (!loading && !previewLoading && !error && data) {
      const reports = buildComparisonReports(data, {
        projectId: parseInt(projectId),
        reportJob: previewReportJob,
        nameSuffix: previewLabel.suffix,
      });
      setProjectReports(reports);
      setProjectName(
        data.projects.nodes.length > 1
          ? 'Multi-Project Comparison'
          : data.projects.nodes[0]?.name ?? previewReportJob?.projectName,
      );
      setSelectedProducts((current) =>
        current.map((c) => {
          if (!c.reportId) {
            return c;
          }
          return {
            ...c,
            reportProducts:
              reports.find((f) => f.reportId === c.reportId)?.products ?? [],
          };
        }),
      );
    }
  }, [loading, previewLoading]);

  if (projectCheckLoading) {
    return <LoadingScreen />;
  }

  if (isWrongWorkspace) {
    return (
      <div>
        <h2>{t('errors.accessForbidden', 'Access Forbidden')}</h2>
        <p>
          {t(
            'errors.noProjectAccess',
            'You do not have access to view this project. Please ensure you are in the correct workspace.',
          )}
        </p>
      </div>
    );
  }

  if (loading || customProductNames.length == 0 || error) {
    return <LoadingScreen />;
  }

  function getProductId(value: string): number {
    if (value.match(/^.*|.*$/)) {
      return parseInt(value.split('|')[0]);
    } else {
      return null;
    }
  }

  function getProductVersion(value: string): string {
    if (value.match(/^.*|.*$/)) {
      return nullCheck(value.split('|')[1]);
    } else {
      return null;
    }
  }

  function projectReportOptions(
    reportProjectId: number,
  ): ComparisonPickerOption<string>[] {
    return projectReports
      .filter((report) => report.projectId == reportProjectId)
      .map((report) => ({ value: report.reportId, label: report.name }));
  }

  function hasCompleteSelections(selectedProducts: ProductToCompareMap[]) {
    return (
      selectedProducts.reduce((a, b) => a && !!b.reportId, true) &&
      selectedProducts.reduce((a, b) => a && !!b.productId, true)
    );
  }

  const projectOptions = withPreviewProject(
    workspaceProjects,
    previewProjectId,
    previewLabel.project,
  );

  return (
    <Paper className={styles.container} style={{ width: `${width - 320}px` }}>
      <div>
        <div className={styles.header}>
          <IconButton
            onClick={browserHistory.goBack}
            size="small"
            style={{ marginLeft: -26 }}
          >
            <KeyboardBackspaceIcon fontSize="small" />
            <PageHeader>{i18n.t('projects.comparison.back')}</PageHeader>
          </IconButton>
        </div>
        <div className={styles.headerTextContainer}>
          <h4 className={styles.summaryTitle}>{projectName}</h4>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ width: COL_WIDTH * selectedProducts.length + 90 }}>
          <div className={styles.selectMain}>
            {selectedProducts.map((d, i) => (
              <div
                className={styles.selectViewContainer}
                style={{ width: COL_WIDTH }}
                key={`picker-${i}`}
              >
                {!(projectsLoading || projectsError) && (
                  <div className={styles.selectView}>
                    <ComparisonPicker
                      id={`project-id-${i}-select`}
                      label={t('projects.comparison.selectedProject')}
                      placeholder={t('projects.comparison.searchProjects')}
                      helperText={t('projects.comparison.typeToSearch', {
                        count: projectOptions.length,
                      })}
                      noOptionsText={t(
                        'projects.comparison.noMatchingProjects',
                      )}
                      options={projectOptions.map((project) => ({
                        value: project.id,
                        label: project.name,
                      }))}
                      value={d.projectId}
                      onChange={(newProjectId) => {
                        setSelectedProducts((current) =>
                          current.map((c, ci) =>
                            ci == i
                              ? {
                                  projectId: newProjectId,
                                  reportId: null,
                                  productId: null,
                                  version: '',
                                  reportProducts: [],
                                  mapOptions:
                                    getDefaultMapOptions(customerPreferences),
                                }
                              : c,
                          ),
                        );
                      }}
                    />
                  </div>
                )}
                <div className={styles.selectView}>
                  <ComparisonPicker
                    id={`report-id-${i}-select`}
                    label={t('projects.comparison.selectedReport')}
                    placeholder={t('projects.comparison.searchReports')}
                    helperText={t('projects.comparison.typeToSearch', {
                      count: projectReportOptions(d.projectId).length,
                    })}
                    noOptionsText={t('projects.comparison.noMatchingReports')}
                    options={projectReportOptions(d.projectId)}
                    value={d.reportId}
                    onChange={(newReportId) => {
                      setSelectedProducts((current) =>
                        current.map((c, ci) =>
                          ci == i
                            ? {
                                projectId: c.projectId,
                                reportId: newReportId,
                                productId: null,
                                version: '',
                                reportProducts:
                                  projectReports.find(
                                    (f) => f.reportId === newReportId,
                                  )?.products ?? [],
                                mapOptions:
                                  getDefaultMapOptions(customerPreferences),
                              }
                            : c,
                        ),
                      );
                    }}
                  />
                </div>
                <div className={styles.selectView}>
                  {d.reportProducts && isProductCompareTab(currentTabIndex) && (
                    <ComparisonPicker
                      id={`product-id-${i}-select`}
                      label={t('projects.comparison.product')}
                      placeholder={
                        d.reportProducts.length
                          ? t('projects.comparison.searchProducts')
                          : t('projects.comparison.selectReportFirst')
                      }
                      helperText={t('projects.comparison.typeToSearch', {
                        count: d.reportProducts.length,
                      })}
                      noOptionsText={t(
                        'projects.comparison.noMatchingProducts',
                      )}
                      disabled={!d.reportProducts.length}
                      options={d.reportProducts.map((product) => ({
                        value: new ProductVersion(product.id, product.version)
                          .value,
                        label: [
                          UPPER_ALPHABET[i],
                          getProductName({
                            productNames: customProductNames,
                            productId: product.id,
                            version: product.version,
                          }),
                        ].join(' - '),
                      }))}
                      value={
                        d.productId === null
                          ? null
                          : new ProductVersion(d.productId, d.version).value
                      }
                      onChange={(newProductValue) => {
                        setSelectedProducts((current) =>
                          current.map((c, ci) =>
                            ci != i
                              ? c
                              : {
                                  ...d,
                                  productId: getProductId(newProductValue),
                                  version: getProductVersion(newProductValue),
                                },
                          ),
                        );
                      }}
                    />
                  )}
                </div>
              </div>
            ))}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-evenly',
                marginTop: '42px',
                marginBottom: '30px',
                width: '64px',
              }}
            >
              {selectedProducts.length < 10 && (
                <MaterialButton
                  variant="outlined"
                  soft
                  onClick={() =>
                    setSelectedProducts((current) =>
                      current.concat([
                        {
                          projectId: parseInt(projectId),
                          reportId: null,
                          productId: null,
                          version: null,
                          reportProducts: [],
                          mapOptions: getDefaultMapOptions(customerPreferences),
                        },
                      ]),
                    )
                  }
                >
                  +
                </MaterialButton>
              )}
              <MaterialButton
                variant="outlined"
                soft
                onClick={() =>
                  setSelectedProducts((current) => current.slice(0, -1))
                }
              >
                -
              </MaterialButton>
            </div>
          </div>
        </div>
        <div style={{ width: COL_WIDTH * selectedProducts.length + 90 }}>
          <Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={currentTabIndex}
                onChange={handleTabChange}
                sx={{ width: '100%' }}
              >
                <Tab
                  label={t('projects.comparison.flavorProfileTab')}
                  value={ComparisonTabs.FLAVOR_PROFILE}
                />
                <Tab
                  label={t('projects.comparison.flavorCompositionTab')}
                  value={ComparisonTabs.FLAVOR_DECOMPOSITION}
                  disabled={selectedProducts.length != 2}
                />
                <Tab
                  label={t('projects.comparison.pqDistributionTab')}
                  value={ComparisonTabs.PQ_DISTRIBUTION}
                />
                <Tab
                  label={t('projects.comparison.stackRankTab')}
                  value={ComparisonTabs.STACK_RANK}
                />
                <Tab
                  label={t('projects.comparison.marketMapTab')}
                  value={ComparisonTabs.MARKET_MAP}
                />
                <Tab
                  label={t('projects.comparison.textureTab')}
                  value={ComparisonTabs.TEXTURE}
                />
              </Tabs>
            </Box>
            <TabPanel
              value={currentTabIndex}
              index={ComparisonTabs.FLAVOR_PROFILE}
            >
              <Box mt={2}>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {selectedProducts.map((d, i) => (
                    <div
                      key={`GastrographRadar-${i}`}
                      style={{ width: `${COL_WIDTH}px`, flexShrink: '0' }}
                    >
                      {d.reportId && d.productId ? (
                        <GastrographRadar
                          title={getProductName({
                            productNames: customProductNames,
                            productId: d.productId,
                            version: d.version,
                          })}
                          productVersion={d.version}
                          titleCenter={true}
                          reportId={d.reportId}
                          productId={d.productId}
                          productDisplayName={getProductName({
                            productNames: customProductNames,
                            productId: d.productId,
                            version: d.version,
                          })}
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
              </Box>
            </TabPanel>
            <TabPanel
              value={currentTabIndex}
              index={ComparisonTabs.FLAVOR_DECOMPOSITION}
            >
              <Grid container>
                <Grid item xs={12}>
                  {selectedProducts[0]?.reportId &&
                  selectedProducts[1]?.reportId &&
                  selectedProducts[0]?.productId &&
                  selectedProducts[1]?.productId ? (
                    <FlavorDecompositionComparison
                      firstVersion={selectedProducts[0].version}
                      secondVersion={selectedProducts[1].version}
                      firstReportId={selectedProducts[0].reportId}
                      firstProductId={selectedProducts[0].productId}
                      firstProductDisplayName={getProductName({
                        productNames: customProductNames,
                        productId: selectedProducts[0].productId,
                        version: selectedProducts[0].version,
                      })}
                      secondReportId={selectedProducts[1].reportId}
                      secondProductId={selectedProducts[1].productId}
                      secondProductDisplayName={getProductName({
                        productNames: customProductNames,
                        productId: selectedProducts[1].productId,
                        version: selectedProducts[1].version,
                      })}
                    />
                  ) : null}
                </Grid>
              </Grid>
            </TabPanel>
            <TabPanel
              value={currentTabIndex}
              index={ComparisonTabs.PQ_DISTRIBUTION}
            >
              <Grid container>
                <Grid item xs={12} style={{ width: '100%' }}>
                  {hasCompleteSelections(selectedProducts) && (
                    <ProductMarketPreference
                      projectId={parseInt(projectId)}
                      customProductNames={customProductNames}
                      products={selectedProducts}
                    />
                  )}
                </Grid>
              </Grid>
            </TabPanel>
            <TabPanel value={currentTabIndex} index={ComparisonTabs.STACK_RANK}>
              <div style={{ display: 'flex', gap: '10px' }}>
                {selectedProducts.map(
                  (p, i) =>
                    p.reportId && (
                      <ReportSummaryComparison
                        key={`ReportSummaryComparison-${p.reportId}-${i}`}
                        projectId={parseInt(projectId)}
                        reportId={p.reportId}
                        hidePercentiles={true}
                      />
                    ),
                )}
              </div>
            </TabPanel>
            <TabPanel value={currentTabIndex} index={ComparisonTabs.MARKET_MAP}>
              <div style={{ display: 'flex', gap: '10px' }}>
                {selectedProducts.map(
                  (p, i) =>
                    p.reportId && (
                      <div
                        key={`ReportMarketMap-${p.reportId}-${i}`}
                        style={{ padding: 11 }}
                      >
                        <ReportMarketMap
                          projectId={parseInt(projectId)}
                          reportId={p.reportId}
                          parentReportId={null}
                          workspaceId={producerId}
                          userId={viewerUserId}
                          showTitle={false}
                          showLegend={false}
                          excludedProducts={new ProductVersionSet()}
                          setExcludedProducts={() => {}}
                          flavorMapOptions={p.mapOptions}
                          setFlavorMapOptions={(newOptions) =>
                            setSelectedProducts((current) =>
                              current.map((d, di) =>
                                di == i
                                  ? {
                                      ...d,
                                      mapOptions: newOptions,
                                    }
                                  : d,
                              ),
                            )
                          }
                        />
                      </div>
                    ),
                )}
              </div>
            </TabPanel>
            <TabPanel value={currentTabIndex} index={ComparisonTabs.TEXTURE}>
              <Box mt={2}>
                <div
                  style={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'stretch',
                  }}
                >
                  {selectedProducts.map((d, i) => (
                    <React.Fragment key={`ReportTextureMap-${d.reportId}-${i}`}>
                      <div style={{ padding: 11 }}>
                        <ReportTextureMap
                          projectId={parseInt(projectId)}
                          reportId={d.reportId ?? NULL_REPORT_ID}
                          parentReportId={null}
                          workspaceId={producerId}
                          showTitle={false}
                          showLegend={false}
                          textureMapOptions={d.mapOptions}
                          useTextureClusterName={true}
                          setTextureMapOptions={(newOptions) =>
                            setSelectedProducts((current) =>
                              current.map((d, di) =>
                                di == i ? { ...d, mapOptions: newOptions } : d,
                              ),
                            )
                          }
                          excludedProducts={new ProductVersionSet()}
                          setExcludedProducts={() => {}}
                        />
                      </div>
                      {i < selectedProducts.length - 1 && (
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{
                            height: 'auto',
                            my: 2,
                            borderRightWidth: 2,
                          }}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </Box>
            </TabPanel>
          </Box>
        </div>
      </div>
    </Paper>
  );
};

// Comparison charts look up product names through the QA preview context
const ProjectComparison: React.FC<Props> = (props) => {
  const { firstReportId, projectId } = props.match.params;
  const qaPreview = useQaPreviewSession(firstReportId, projectId);
  return (
    <QaPreviewReportProvider session={qaPreview}>
      <ProjectComparisonContent {...props} />
    </QaPreviewReportProvider>
  );
};

export default ProjectComparison;
